import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from './api/axios';
import { useAuth } from './contexts/KeycloakContext';
import LoadingScreen from './layout/LoadingScreen';
import { isAdmin, isSuperadmin } from './utils/roleUtils';
import Sidebar from './layout/Sidebar';
import { useWorkspace } from './contexts/WorkspaceContext';
import { CreditProvider } from './contexts/CreditContext';
import { NavigationGuardProvider } from './contexts/NavigationGuardContext';
import { io } from 'socket.io-client';

// Pages
import AdminDashboard from './features/admin/AdminDashboard';
import ProspectFinderView from './features/prospecting/ProspectFinderView';
import MarketAnalysisView from './features/analysis/MarketAnalysisView';
import ProspectListView from './features/prospecting/ProspectListView';
import AnalysisReportView from './features/analysis/AnalysisReportView';
import UnifiedLeadGenView from './features/search/UnifiedLeadGenView';
import Settings from './features/settings/Settings';
import TierManagement from './features/settings/TierManagement';
import PaymentSuccess from './features/subscription/PaymentSuccess';
import Members from './features/workspace/Members';
import OrgSettings from './features/workspace/OrgSettings';
import AcceptInvite from './features/workspace/AcceptInvite';
import CreditManagement from './features/credits/CreditManagement';
import SuperadminCreditUsage from './features/admin/SuperadminCreditUsage';

import PersonalCreditUsage from './features/workspace/PersonalCreditUsage';
import CustomerManagementSystem from './features/crm/CustomerManagementSystem';
import Campaign from './features/campaign/Campaign';
import EmailOutreachView from './features/emailOutreach/EmailOutreachView';
import MeetingsView from './features/meetings/MeetingsView';
import AnalyticsModule from './features/analytics/AnalyticsModule';
import SeoAieoView from './features/seo/SeoAieoView';
import LandingPageBuilderView from './features/landingPages/LandingPageBuilderView';
import KnowledgeBaseView from './features/knowledge/KnowledgeBaseView';
import PublicBookingView from './features/meetings/components/PublicBookingView';
import ContactSales from './features/subscription/ContactSales';
import CreditInsightReport from './features/credits/CreditInsightReport';
import HelpCenter from './features/help/HelpCenter';
import SupportAdmin from './features/admin/SupportAdmin';
import PolicyCheckpoint from './features/admin/PolicyCheckpoint';
import AccessDenied from './features/common/AccessDenied';

import AuthCallback from './features/auth/AuthCallback';
import Login from './features/auth/Login';
import Signup from './features/auth/Signup';
import ForgotPassword from './features/auth/ForgotPassword';
import TermsAndConditions from './features/terms-and-privacy/TermsAndConditions';
import PrivacyPolicy from './features/terms-and-privacy/PrivacyPolicy';
import WorkspaceProtectedRoute from './layout/WorkspaceProtectedRoute';
import MFAGate from './layout/MFAGate';
import './App.css';

// Chat Widget
import ShowChatWidget from './features/chatWidget/ShowChatWidget';

// Local dev: explicit backend host from .env (e.g. http://localhost:5001)
// Staging/Prod: frontend and backend share the same origin via nginx, so
// io() can connect to the current page origin and reach /socket.io/
const SOCKET_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001');

function App() {
  const { keycloak, authenticated, initialized } = useAuth();
  const {
    isOwner,
    isOrganizationAdmin,
    isSuperadmin: isWorkspaceSuperadmin,
    workspaceId,
  } = useWorkspace();
  const canSeeBilling = isWorkspaceSuperadmin || isOwner || isOrganizationAdmin;
  useEffect(() => {
    return () => {};
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const [routeRestored, setRouteRestored] = useState(false);

  // Map URL path to activeView
  const getActiveViewFromPath = (pathname) => {
    if (pathname === '/admin/support') return 'admin-support';
    if (pathname === '/admin/policy' || pathname === '/admin/policy-checkpoint')
      return 'policy-checkpoint';
    if (pathname === '/admin' || pathname === '/admin/dashboard') return 'admin-dashboard';
    if (pathname === '/admin/user-management') return 'user-management';
    if (pathname === '/admin/audit-logs') return 'audit-logs';
    if (pathname === '/admin/api-usage') return 'api-usage';
    if (pathname === '/admin/credits') return 'admin-credits';
    if (pathname === '/admin/organization-management') return 'organization-management';
    if (pathname === '/prospects') return 'prospect-finder';
    if (pathname === '/market-analysis') return 'market-analysis';
    if (pathname === '/prospect-list') return 'prospect-list';
    if (pathname === '/saved-list') return 'saved-list';
    if (pathname === '/sent-emails') return 'sent-emails';
    if (pathname === '/analysis-reports') return 'analysis-reports';
    if (pathname === '/settings') return 'settings';
    if (pathname === '/subscription') return 'subscription';
    if (pathname === '/payment-success') return 'payment-success';
    if (pathname === '/members') return 'members';
    if (pathname === '/org-settings') return 'org-settings';
    if (pathname === '/activity-logs') return 'activity-logs';
    if (pathname === '/workspace/credits') return 'workspace-credits';
    if (pathname === '/credit-management') return 'workspace-credits';
    if (pathname === '/help') return 'help';
    if (pathname === '/contact-sales') return 'contact-sales';
    if (pathname === '/b2c-recommend') return 'b2c-recommend';
    if (pathname === '/customer-management') return 'customer-management';
    if (pathname === '/meetings') return 'meetings';
    if (pathname === '/campaign' || pathname === '/campaigns') return 'campaign';
    if (pathname === '/email-outreach' || pathname.startsWith('/email-outreach/'))
      return 'email-outreach';
    if (pathname === '/seo-aieo') return 'seo-aieo';
    if (pathname === '/knowledge' || pathname.startsWith('/knowledge/')) return 'knowledge';
    if (pathname === '/landing-pages') return 'landing-pages';
    if (
      pathname === '/analytics' ||
      pathname.startsWith('/analytics/') ||
      pathname === '/ai-assistant' ||
      pathname.startsWith('/ai-assistant/')
    )
      return 'analytics';
    if (pathname === '/credits') return 'personal-credits';
    if (pathname === '/discover' || pathname.startsWith('/discover/') || pathname === '/')
      return 'discover';
    // Default to discover for all users
    return 'discover';
  };

  const [currentUser, setCurrentUser] = useState(null);

  // Initialize sidebar closed state from localStorage
  const getInitialSidebarClosed = () => {
    const saved = localStorage.getItem('sidebarClosed');
    return saved === 'true';
  };

  // Determine activeView based on current URL path
  const [activeView, setActiveView] = useState(() => getActiveViewFromPath(location.pathname));
  const prevViewRef = useRef(activeView);

  // Store current route in sessionStorage on every location change
  useEffect(() => {
    if (
      authenticated &&
      location.pathname !== '/login' &&
      location.pathname !== '/signup' &&
      location.pathname !== '/callback'
    ) {
      sessionStorage.setItem('lastRoute', location.pathname);
    }
  }, [location.pathname, authenticated]);

  // Store intended destination when not authenticated
  useEffect(() => {
    if (
      !authenticated &&
      initialized &&
      location.pathname !== '/login' &&
      location.pathname !== '/signup' &&
      location.pathname !== '/forgot-password' &&
      location.pathname !== '/callback'
    ) {
      sessionStorage.setItem('intended_destination', location.pathname);
    }
  }, [authenticated, initialized, location.pathname]);

  // Sync activeView with URL path whenever location changes
  useEffect(() => {
    const newView = getActiveViewFromPath(location.pathname);
    const prevView = prevViewRef.current;

    // Clear prospect finder cache and reset filters when leaving prospect-finder view
    if (prevView === 'prospect-finder' && newView !== 'prospect-finder') {
      localStorage.removeItem('lusha_prospects_cache');
      localStorage.removeItem('lusha_companies_cache');
      // Reset filters to default
      setFilters(defaultProspectFilters);
      setSearchQuery('');
      setSearchType('prospects');
    }

    // Update current view
    setActiveView(newView);
    prevViewRef.current = newView;

    // Refresh profile on navigation to ensure balance/tier is up to date
    if (authenticated) {
      fetchUserProfile();
    }

    // Close prospect filter for non-prospect-finder views
    if (newView !== 'prospect-finder') {
      setProspectFilterExpanded(false);
    }
  }, [location.pathname]);

  const [sidebarClosed, setSidebarClosed] = useState(getInitialSidebarClosed());

  // Initialize prospectFilterExpanded based on localStorage or default to closed
  // Initialize prospectFilterExpanded based on localStorage or default to expanded
  const getInitialProspectFilterExpanded = () => {
    // First check if there's a saved state in localStorage
    const saved = localStorage.getItem('prospectFilterExpanded');
    if (saved !== null) {
      return saved === 'true';
    }
    // Default to closed (false) for all users on login
    return false;
  };

  const [prospectFilterExpanded, setProspectFilterExpanded] = useState(
    getInitialProspectFilterExpanded()
  );
  const [searchTrigger, setSearchTrigger] = useState({ timestamp: 0, type: 'prospects' });
  const [searchType, setSearchType] = useState('prospects'); // 'prospects' or 'companies'
  const defaultProspectFilters = {
    jobTitles: [],
    locations: [],
    managementLevels: [],
    departments: [],
    companyNames: [],
    industries: [],
    companySizes: [],
    companyRevenues: [],
    skills: [],
  };
  const [filters, setFilters] = useState({
    jobTitles: [],
    locations: [],
    managementLevels: [],
    departments: [],
    companyNames: [],
    industries: [],
    companySizes: [],
    companyRevenues: [],
    skills: [],
  });
  const [resetSearchSignal, setResetSearchSignal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [userCreditStatus, setUserCreditStatus] = useState('available');

  const mapUsagePctToStatus = useCallback((usagePct) => {
    const pct = Number(usagePct || 0);
    if (pct >= 100) return 'limited';
    if (pct >= 70) return 'warning';
    return 'available';
  }, []);

  const lastProfileFetchRef = useRef(0);
  const isFetchingProfileRef = useRef(false);

  // Fetch detailed user profile from backend
  const fetchUserProfile = useCallback(
    async (forced = false) => {
      if (!authenticated || !keycloak.token) return;

      // Minimum 1.5 seconds between fetches unless forced
      const now = Date.now();
      if (!forced && now - lastProfileFetchRef.current < 1500) {
        return;
      }

      if (isFetchingProfileRef.current) return;
      isFetchingProfileRef.current = true;
      lastProfileFetchRef.current = now;

      try {
        const response = await axios.get('/profile');
        if (response.data.success && response.data.data) {
          const profile = response.data.data;

          setCurrentUser((prevUser) => {
            const updatedUser = {
              ...(prevUser || {}),
              userId: profile.id,
              email: profile.email || prevUser?.email || '',
              username: profile.username || prevUser?.username || 'user',
              tier: profile.tier || 'Free',
              creditBalance: profile.credit_balance || 0,
              creditLimit: profile.credit_limit || 0,
              account_type: profile.account_type || 'personal',
              isOAuthUser: profile.isOAuthUser === true || prevUser?.isOAuthUser === true,
              warnings: profile.warnings || [],
              pending_tier: profile.pending_tier || null,
              pending_tier_effective_at: profile.pending_tier_effective_at || null,
              subscription_status: profile.subscription_status || null,
              subscription_period_end: profile.subscription_period_end || null,
              stripe_subscription_id: profile.stripe_subscription_id || null,
            };

            const balance = Number(profile.credit_balance || 0);
            const limit = Number(profile.credit_limit || 0);

            let status = 'available';
            if (limit > 0) {
              const usagePct = ((limit - balance) / limit) * 100;
              status = mapUsagePctToStatus(usagePct);
            } else {
              if (balance <= 0) status = 'limited';
              else if (balance <= 5) status = 'warning';
            }

            if (balance <= 0) status = 'limited';
            setUserCreditStatus(status);

            return updatedUser;
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        isFetchingProfileRef.current = false;
      }
    },
    [authenticated, keycloak.token, mapUsagePctToStatus]
  );

  // Redundant safety check to ensure profile is synced
  useEffect(() => {
    if (authenticated && keycloak.token && keycloak.tokenParsed?.sub) {
      const userId = keycloak.tokenParsed.sub;
      const ensKey = `ens_v3_${userId}`;
      if (!sessionStorage.getItem(ensKey)) {
        axios
          .post('/profile/ensure', { userId })
          .then(() => {
            sessionStorage.setItem(ensKey, 'true');
            fetchUserProfile(true);
          })
          .catch((err) => console.error('[App] Ensure failed:', err));
      }
    }
  }, [authenticated, keycloak.token, keycloak.tokenParsed?.sub, fetchUserProfile]);

  // Initialize user from Keycloak when authenticated
  useEffect(() => {
    if (initialized && authenticated) {
      const firstName = keycloak.tokenParsed?.given_name || '';
      const lastName = keycloak.tokenParsed?.family_name || '';
      const email = keycloak.tokenParsed?.email || '';
      const userId = keycloak.tokenParsed?.sub || '';

      const tokenParsed = keycloak.tokenParsed;
      const isOAuthUser = !!(
        tokenParsed?.identity_provider ||
        tokenParsed?.idp ||
        tokenParsed?.idp_alias ||
        tokenParsed?.iss?.includes('google') ||
        tokenParsed?.sub?.includes(':') ||
        tokenParsed?.federated_identities?.length > 0
      );

      const username =
        tokenParsed?.preferred_username || tokenParsed?.email?.split('@')[0] || 'user';
      const userIsAdmin = isAdmin(keycloak);
      const userIsSuperadmin = isSuperadmin(keycloak);

      let userRole = 'user';
      if (userIsSuperadmin) userRole = 'superadmin';
      else if (userIsAdmin) userRole = 'admin';

      const user = {
        userId,
        username,
        email,
        firstName,
        lastName,
        isAdmin: userIsAdmin,
        isSuperadmin: userIsSuperadmin,
        role: userRole,
        isOAuthUser: isOAuthUser,
      };

      setCurrentUser(user);
      fetchUserProfile(true); // Forced initial fetch

      localStorage.setItem('userId', userId);
      localStorage.setItem('username', username);
      localStorage.setItem('email', email);
      localStorage.setItem('isAdmin', String(userIsAdmin));

      if (userIsAdmin) {
        setProspectFilterExpanded(false);
      }
    } else if (initialized && !authenticated) {
      setCurrentUser(null);
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('email');
      localStorage.removeItem('isAdmin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, authenticated, keycloak]);

  // Periodic profile refresh (every 60 seconds for better auto-update)
  useEffect(() => {
    if (authenticated && fetchUserProfile) {
      const interval = setInterval(() => {
        fetchUserProfile();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [authenticated, fetchUserProfile, keycloak]);

  // Listen for profile refresh events from other components
  useEffect(() => {
    const handleRefresh = () => {
      if (authenticated && fetchUserProfile) {
        fetchUserProfile(true); // Force refresh to bypass throttling
      }
    };
    window.addEventListener('refresh-profile', handleRefresh);
    return () => window.removeEventListener('refresh-profile', handleRefresh);
  }, [authenticated, fetchUserProfile]);

  // Socket.io for real-time credit updates, notifications, and live workspace CRM sync
  useEffect(() => {
    if (!authenticated || !keycloak?.token) return;

    // Socket connects directly to backend — don't route through /api proxy
    const socket = io(SOCKET_URL, {
      auth: (cb) => {
        cb({ token: keycloak?.token || '' });
      },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      if (workspaceId) {
        socket.emit('join_workspace', workspaceId);
      }
      window.dispatchEvent(new CustomEvent('socket-reconnected'));
    });

    socket.on('credit-update', (data) => {
      // Update currentUser creditBalance
      setCurrentUser((prev) => {
        if (prev) {
          return { ...prev, creditBalance: data.credit_balance };
        }
        return prev;
      });
      // Broadcast to all contexts/components so sidebar updates instantly
      const event = new CustomEvent('credit-update', { detail: data });
      window.dispatchEvent(event);
    });

    socket.on('new-notification', (data) => {
      const event = new CustomEvent('new-notification', { detail: data });
      window.dispatchEvent(event);
    });

    socket.on('lead:changed', (data) => {
      const event = new CustomEvent('lead-changed', { detail: data });
      window.dispatchEvent(event);
    });

    socket.on('crm:pipeline_stages_updated', (data) => {
      const event = new CustomEvent('crm-pipeline-stages-updated', { detail: data });
      window.dispatchEvent(event);
    });

    return () => {
      if (workspaceId) {
        socket.emit('leave_workspace', workspaceId);
      }
      socket.disconnect();
    };
  }, [authenticated, keycloak?.token, workspaceId]);

  // Restore last route after authentication
  useEffect(() => {
    if (authenticated && !routeRestored) {
      // If there is a pending workspace invite, let Login.js + AcceptInvite handle navigation
      const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingInviteToken) {
        setRouteRestored(true);
        return;
      }

      const lastRoute = sessionStorage.getItem('lastRoute');
      if (
        lastRoute &&
        lastRoute !== location.pathname &&
        lastRoute !== '/login' &&
        lastRoute !== '/signup' &&
        lastRoute !== '/callback'
      ) {
        navigate(lastRoute, { replace: true });
      }
      setRouteRestored(true);
    }
  }, [authenticated, location.pathname, navigate, routeRestored]);

  // Persist prospectFilterExpanded state to localStorage
  useEffect(() => {
    localStorage.setItem('prospectFilterExpanded', prospectFilterExpanded.toString());
  }, [prospectFilterExpanded]);

  // Persist sidebarClosed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarClosed', sidebarClosed.toString());
  }, [sidebarClosed]);

  // Clean up interval on unmount or auth change
  useEffect(() => {
    return () => {
      // Intervals are handled inside the auth useEffect, but we should be careful.
      // Actually, let's keep it simple for now.
    };
  }, []);

  const handleLogout = async () => {
    try {
      // Import authService at the top level
      const authService = await import('./services/keycloak/authService');

      // Use the authService logout which properly clears all tokens and session data
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);

      // Fallback: manually clear everything and redirect
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  };

  const toggleSidebar = () => {
    setSidebarClosed(!sidebarClosed);
  };

  const toggleProspectFilter = () => {
    if (sidebarClosed) {
      setSidebarClosed(false);
      setTimeout(() => {
        setProspectFilterExpanded(true);
      }, 350);
    } else {
      setProspectFilterExpanded(!prospectFilterExpanded);
    }
  };

  const handleViewChange = (view, newSearchType) => {
    if (view === activeView && view === 'prospect-finder') {
      setResetSearchSignal((prev) => prev + 1);
    }
    setActiveView(view);
    // Update search type if provided
    if (newSearchType && (newSearchType === 'prospects' || newSearchType === 'companies')) {
      setSearchType(newSearchType);
    }
    // Persist active view to localStorage
    localStorage.setItem('activeView', view);
    if (view !== 'prospect-finder') {
      setProspectFilterExpanded(false);
    }

    // Navigate to the correct route
    const routeMap = {
      'admin-dashboard': '/admin',
      'api-usage': '/admin/api-usage',
      'admin-credits': '/admin/credits',
      'organization-management': '/admin/organization-management',
      'prospect-finder': '/prospects',
      'market-analysis': '/market-analysis',
      'prospect-list': '/prospect-list',
      'saved-list': '/saved-list',
      'sent-emails': '/sent-emails',
      'analysis-reports': '/analysis-reports',
      settings: '/settings',
      members: '/members',
      'org-settings': '/org-settings',
      'activity-logs': '/activity-logs',
      'b2c-recommend': '/b2c-recommend',
      knowledge: '/knowledge',
      'email-outreach': '/email-outreach',
      discover: '/discover',
      help: '/help',
    };

    const route = routeMap[view];
    if (route && location.pathname !== route) {
      navigate(route);
    }
  };

  const handleSearch = (searchFilters) => {
    // Trigger search with timestamp and current type
    setSearchTrigger({ timestamp: Date.now(), type: searchType });
  };

  // Wait for auth and workspace initialization
  const { loading: workspaceLoading } = useWorkspace();

  if (!initialized || workspaceLoading) {
    return <LoadingScreen fullScreen={true} />;
  }

  // If not authenticated, show auth pages
  if (!authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/callback" element={<AuthCallback />} />
        {/* Allow landing on accept-invite when not yet logged in; component will save token and redirect to login */}
        <Route path="/workspace/accept-invite" element={<AcceptInvite />} />
        {/* Public legal & booking pages - accessible without authentication */}
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/book/:slug" element={<PublicBookingView />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated - show main app
  // Check if current path is a standalone public page - render without sidebar layout
  const isStandalonePublicPage =
    location.pathname === '/terms' ||
    location.pathname === '/privacy' ||
    location.pathname.startsWith('/book/');

  if (isStandalonePublicPage) {
    return (
      <CreditProvider>
        <Routes>
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/book/:slug" element={<PublicBookingView />} />
        </Routes>
      </CreditProvider>
    );
  }

  return (
    <CreditProvider>
      <MFAGate>
        <NavigationGuardProvider>
          <div className="app">
            <Sidebar
              activeView={activeView}
              onViewChange={handleViewChange}
              sidebarClosed={sidebarClosed}
              onToggleSidebar={toggleSidebar}
              prospectFilterExpanded={prospectFilterExpanded}
              onToggleProspectFilter={toggleProspectFilter}
              filters={filters}
              setFilters={setFilters}
              userId={currentUser?.userId}
              onSearch={handleSearch}
              searchType={searchType}
              currentUser={currentUser}
              onLogout={handleLogout}
              isAdmin={currentUser?.isAdmin}
              userCreditStatus={userCreditStatus}
            />

            {/* Backdrop for mobile sidebar */}
            {!sidebarClosed && (
              <div
                className="sidebar-backdrop"
                onClick={() => setSidebarClosed(true)}
                aria-hidden="true"
              />
            )}

            <div className="main-content">
              <Routes>
                <Route path="/callback" element={<AuthCallback />} />

                <Route
                  path="/"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <UnifiedLeadGenView
                        userId={currentUser?.userId}
                        refreshProfile={fetchUserProfile}
                      />
                    )
                  }
                />

                <Route
                  path="/admin"
                  element={
                    currentUser?.isAdmin ? (
                      <AdminDashboard
                        initialTab="dashboard"
                        currentUser={currentUser}
                        refreshProfile={fetchUserProfile}
                      />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="/admin/policy-checkpoint"
                  element={
                    currentUser?.isAdmin ? (
                      <PolicyCheckpoint currentUser={currentUser} />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/policy"
                  element={<Navigate to="/admin/policy-checkpoint" replace />}
                />

                <Route
                  path="/admin/api-usage"
                  element={
                    currentUser?.isAdmin ? (
                      <AdminDashboard
                        initialTab="credits"
                        currentUser={currentUser}
                        refreshProfile={fetchUserProfile}
                      />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="/admin/credits"
                  element={
                    currentUser?.isAdmin ? <SuperadminCreditUsage /> : <Navigate to="/" replace />
                  }
                />

                <Route
                  path="/subscription"
                  element={
                    currentUser?.isAdmin || !canSeeBilling ? (
                      <Navigate to="/" replace />
                    ) : (
                      <TierManagement currentUser={currentUser} setCurrentUser={setCurrentUser} />
                    )
                  }
                />

                <Route path="/credit-management" element={<CreditManagement />} />

                <Route path="/credit-insights" element={<CreditInsightReport />} />

                <Route
                  path="/workspace/credits"
                  element={<Navigate to="/credit-management" replace />}
                />

                <Route
                  path="/credits"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <PersonalCreditUsage />
                    )
                  }
                />

                <Route
                  path="/customer-management"
                  element={
                    <WorkspaceProtectedRoute>
                      <CustomerManagementSystem />
                    </WorkspaceProtectedRoute>
                  }
                />

                <Route
                  path="/meetings"
                  element={
                    <WorkspaceProtectedRoute>
                      <MeetingsView />
                    </WorkspaceProtectedRoute>
                  }
                />

                <Route
                  path="/campaign"
                  element={
                    <WorkspaceProtectedRoute>
                      <Campaign />
                    </WorkspaceProtectedRoute>
                  }
                />

                <Route path="/campaigns" element={<Navigate to="/campaign" replace />} />

                <Route
                  path="/email-outreach"
                  element={
                    <WorkspaceProtectedRoute>
                      <EmailOutreachView />
                    </WorkspaceProtectedRoute>
                  }
                />

                <Route
                  path="/seo-aieo"
                  element={
                    <WorkspaceProtectedRoute>
                      <SeoAieoView />
                    </WorkspaceProtectedRoute>
                  }
                />

                <Route
                  path="/knowledge"
                  element={
                    <WorkspaceProtectedRoute>
                      <KnowledgeBaseView />
                    </WorkspaceProtectedRoute>
                  }
                />

                <Route
                  path="/landing-pages"
                  element={
                    <WorkspaceProtectedRoute>
                      <LandingPageBuilderView />
                    </WorkspaceProtectedRoute>
                  }
                />

                {/* Analytics and Attribution module (dashboard tabs + AI Assistant tab) */}
                {['/analytics', '/analytics/:tab', '/ai-assistant', '/ai-assistant/:roomId'].map(
                  (path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <WorkspaceProtectedRoute>
                          <AnalyticsModule />
                        </WorkspaceProtectedRoute>
                      }
                    />
                  )
                )}

                <Route path="/access-denied" element={<AccessDenied />} />

                <Route
                  path="/admin/user-management"
                  element={
                    currentUser?.isAdmin ? (
                      <AdminDashboard
                        initialTab="user-management"
                        currentUser={currentUser}
                        refreshProfile={fetchUserProfile}
                      />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="/admin/organization-management"
                  element={
                    currentUser?.isAdmin ? (
                      <AdminDashboard
                        initialTab="organization-management"
                        currentUser={currentUser}
                        refreshProfile={fetchUserProfile}
                      />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route
                  path="/admin/audit-logs"
                  element={
                    currentUser?.isAdmin ? (
                      <AdminDashboard
                        initialTab="audit-logs"
                        currentUser={currentUser}
                        refreshProfile={fetchUserProfile}
                      />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                <Route path="/prospects" element={<Navigate to="/" replace />} />

                <Route
                  path="/market-analysis"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <MarketAnalysisView
                        userId={currentUser?.userId}
                        refreshProfile={fetchUserProfile}
                      />
                    )
                  }
                />

                <Route
                  path="/prospect-list"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <ProspectListView userId={currentUser?.userId} />
                    )
                  }
                />
                <Route
                  path="/saved-list"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <ProspectListView userId={currentUser?.userId} pageTitle="Saved List" />
                    )
                  }
                />
                <Route
                  path="/analysis-reports"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <AnalysisReportView userId={currentUser?.userId} />
                    )
                  }
                />

                <Route
                  path="/discover"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <UnifiedLeadGenView
                        userId={currentUser?.userId}
                        refreshProfile={fetchUserProfile}
                      />
                    )
                  }
                />
                <Route
                  path="/discover/:chatRoomId"
                  element={
                    <UnifiedLeadGenView
                      userId={currentUser?.userId}
                      refreshProfile={() => fetchUserProfile(true)}
                    />
                  }
                />
                <Route path="/" element={<Navigate to="/discover" replace />} />

                <Route
                  path="/settings"
                  element={<Settings currentUser={currentUser} setCurrentUser={setCurrentUser} />}
                />

                <Route
                  path="/payment-success"
                  element={
                    currentUser?.isAdmin ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <PaymentSuccess currentUser={currentUser} setCurrentUser={setCurrentUser} />
                    )
                  }
                />
                <Route
                  path="/help"
                  element={currentUser?.isAdmin ? <Navigate to="/admin" replace /> : <HelpCenter />}
                />
                <Route
                  path="/admin/support"
                  element={
                    currentUser?.isAdmin ? (
                      <SupportAdmin currentUser={currentUser} />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route path="/contact-sales" element={<ContactSales />} />

                <Route path="/members" element={<Members />} />
                <Route path="/org-settings" element={<OrgSettings />} />
                <Route path="/activity-logs" element={<Navigate to="/members" replace />} />
                <Route path="/workspace/accept-invite" element={<AcceptInvite />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              {/* Show Chat Widget for all users except admins and superadmin */}
              {!currentUser?.isAdmin && <ShowChatWidget />}
            </div>
          </div>
        </NavigationGuardProvider>
      </MFAGate>
    </CreditProvider>
  );
}

export default App;
